import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import workerSrc from "url:pdfjs-dist/build/pdf.worker.min.mjs";

let workerReadyPromise = null;

function ensurePdfWorker() {
  if (!workerReadyPromise) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;
    workerReadyPromise = Promise.resolve();
  }
  return workerReadyPromise;
}

const myPdfs = {};

function renderPdf(pdfs, canvas, pageno, scale, width) {
  const pdf = pdfs.pdf;
  pdf.getPage(pageno).then(function (page) {
    let viewport = page.getViewport({ scale: scale });

    if (width) {
      const wscale = width / viewport.width;
      const newScale = wscale * scale;
      const height = wscale * viewport.height;
      viewport = page.getViewport({ scale: newScale });
      viewport.width = width;
      viewport.height = height;
    }

    const context = canvas.getContext("2d", { alpha: false });
    const outputScale = getOutputScale(context);

    canvas.width = (viewport.width * outputScale.sx) | 0;
    canvas.height = (viewport.height * outputScale.sy) | 0;
    canvas.style.height = viewport.height + "px";
    canvas.style.width = viewport.width + "px";

    context.scale(outputScale.sx, outputScale.sy);

    pdfs.prevHeight = viewport.height;
    pdfs.prevWidth = viewport.width;

    const renderContext = {
      canvasContext: context,
      viewport: viewport,
    };
    page.render(renderContext);
  });
}

function getOutputScale(ctx) {
  const devicePixelRatio = window.devicePixelRatio || 1;
  const backingStoreRatio =
    ctx.webkitBackingStorePixelRatio ||
    ctx.mozBackingStorePixelRatio ||
    ctx.msBackingStorePixelRatio ||
    ctx.oBackingStorePixelRatio ||
    ctx.backingStorePixelRatio ||
    1;
  const pixelRatio = devicePixelRatio / backingStoreRatio;
  return {
    sx: pixelRatio,
    sy: pixelRatio,
    scaled: pixelRatio !== 1,
  };
}

class PdfViewerCustomElement extends HTMLElement {
  constructor() {
    super();
    const shadow = this.attachShadow({ mode: "open" });
    this.canvas = document.createElement("canvas");
    shadow.appendChild(this.canvas);
  }

  connectedCallback() {
    const name = this.getAttribute("name");
    const page = parseInt(this.getAttribute("page"), 10);
    const widthRaw = Number.parseFloat(this.getAttribute("width"));
    const width = Number.isFinite(widthRaw) ? widthRaw : undefined;

    const pdf = myPdfs[name];
    if (pdf) {
      if (width) {
        this.canvas.width = width;
      } else if (pdf.prevWidth) {
        this.canvas.width = pdf.prevWidth;
      }

      if (pdf.prevHeight) {
        this.canvas.height = pdf.prevHeight;
      }
      renderPdf(pdf, this.canvas, page, 1, width);
    }
  }
}

function formatPdfError(reason) {
  if (reason == null) {
    return "unknown error";
  }
  if (typeof reason === "string") {
    return reason;
  }
  if (typeof reason === "object" && reason.message) {
    return String(reason.message);
  }
  return String(reason);
}

function pdfCommandHandler(app, cmd) {
  if (!app.ports.receivePdfMsg) {
    return;
  }
  if (cmd.cmd === "openurl") {
    pdfjsLib.getDocument({ url: cmd.url }).promise.then(
      function (pdf) {
        myPdfs[cmd.name] = { pdf: pdf };
        app.ports.receivePdfMsg.send({
          msg: "loaded",
          name: cmd.name,
          pageCount: pdf.numPages,
        });
      },
      function (reason) {
        app.ports.receivePdfMsg.send({
          msg: "error",
          name: cmd.name,
          error: formatPdfError(reason),
        });
      },
    );
  } else if (cmd.cmd === "openstring") {
    const buff = Uint8Array.from(atob(cmd.string), (c) => c.charCodeAt(0));
    pdfjsLib.getDocument({ data: buff }).promise.then(
      function (pdf) {
        myPdfs[cmd.name] = { pdf: pdf };
        app.ports.receivePdfMsg.send({
          msg: "loaded",
          name: cmd.name,
          pageCount: pdf.numPages,
        });
      },
      function (reason) {
        app.ports.receivePdfMsg.send({
          msg: "error",
          name: cmd.name,
          error: formatPdfError(reason),
        });
      },
    );
  } else if (cmd.cmd === "close") {
    const entry = myPdfs[cmd.name];
    delete myPdfs[cmd.name];
    if (entry && entry.pdf) {
      entry.pdf.loadingTask.destroy().catch(function () {});
    }
  } else {
    app.ports.receivePdfMsg.send({
      msg: "error",
      name: typeof cmd.name === "string" ? cmd.name : "",
      error: "invalid command to pdf-element: " + cmd.cmd,
    });
  }
}

const PdfPreviewPort = {
  add: function (app) {
    if (typeof customElements !== "undefined" && !customElements.get("pdf-element")) {
      customElements.define("pdf-element", PdfViewerCustomElement);
    }

    if (app.ports.sendPdfCommand) {
      app.ports.sendPdfCommand.subscribe(function (cmd) {
        ensurePdfWorker()
          .then(function () {
            pdfCommandHandler(app, cmd);
          })
          .catch(function (err) {
            if (app.ports.receivePdfMsg) {
              app.ports.receivePdfMsg.send({
                msg: "error",
                name: typeof cmd.name === "string" ? cmd.name : "",
                error: formatPdfError(err),
              });
            }
          });
      });
    }
  },
};

export default PdfPreviewPort;
